"use client";

import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef } from "react";

import { useOnlineStatus } from "@/app/hooks/useOnlineStatus";
import { useSession } from "@/app/hooks/useSession";
import { AccountSection } from "@/components/settings/AccountSection";
import { AgentsSection } from "@/components/settings/AgentsSection";
import { AppearanceSection } from "@/components/settings/AppearanceSection";
import { LegalSection } from "@/components/settings/LegalSection";
import { MealPlanSection } from "@/components/settings/MealPlanSection";
import { ReferralSection } from "@/components/settings/ReferralSection";
import { SubscriptionSection } from "@/components/settings/SubscriptionSection";
import { TutorialSection } from "@/components/settings/TutorialSection";
import { staggerContainerVariants, staggerItemVariants } from "@/lib/animations";

type Props = {
  onUpgradeClick: () => void;
  onCancelClick: () => void;
  onReady: () => void;
  subscriptionRefreshKey: number;
};

export default function SettingsMotionContent({
  onUpgradeClick,
  onCancelClick,
  onReady,
  subscriptionRefreshKey,
}: Props) {
  const t = useTranslations("settings");
  const { isAuthenticated } = useSession();
  const { isOffline } = useOnlineStatus();
  const readyCalled = useRef(false);

  const handleReadyOnce = useCallback(() => {
    if (readyCalled.current) return;
    readyCalled.current = true;
    onReady();
  }, [onReady]);

  // Also mark ready on mount so we signal chunk/component readiness immediately
  // (no timeouts). The ref prevents multiple calls in Strict Mode.
  useEffect(() => {
    handleReadyOnce();
  }, [handleReadyOnce]);

  return (
    <motion.div
      className="space-y-8"
      initial="hidden"
      animate="visible"
      variants={staggerContainerVariants()}
      // Trigger ready as soon as the animation starts so the loader can hide
      // (avoids double-appear when Strict Mode remounts animation). We still
      // keep onAnimationComplete as a fallback.
      onAnimationStart={handleReadyOnce}
      onAnimationComplete={handleReadyOnce}
    >
      {/* Account Section */}
      <motion.section
        className="space-y-4"
        variants={staggerItemVariants}
        initial="hidden"
        animate="visible"
      >
        <h2 className="text-lg font-semibold">{t("account")}</h2>
        <AccountSection onUpgradeClick={onUpgradeClick} />
      </motion.section>

      {/* Appearance Section */}
      <motion.section
        className="space-y-4"
        variants={staggerItemVariants}
        initial="hidden"
        animate="visible"
      >
        <h2 className="text-lg font-semibold">{t("appearance")}</h2>
        <AppearanceSection />
      </motion.section>

      {/* Meal Planning Section */}
      {isAuthenticated && (
        <motion.section
          className="space-y-4"
          variants={staggerItemVariants}
          initial="hidden"
          animate="visible"
        >
          <h2 className="text-lg font-semibold">{t("mealPlanning")}</h2>
          <MealPlanSection />
        </motion.section>
      )}

      {/* Subscription Section */}
      {isAuthenticated && !isOffline && (
        <motion.section
          className="space-y-4"
          variants={staggerItemVariants}
          initial="hidden"
          animate="visible"
        >
          <h2 className="text-lg font-semibold">{t("subscription")}</h2>
          <SubscriptionSection
            subscriptionRefreshKey={subscriptionRefreshKey}
            onCancelClick={onCancelClick}
            onUpgradeClick={onUpgradeClick}
          />
        </motion.section>
      )}

      {/* Referral Section */}
      {isAuthenticated && !isOffline && (
        <motion.section
          className="space-y-4"
          variants={staggerItemVariants}
          initial="hidden"
          animate="visible"
        >
          <h2 className="text-lg font-semibold">{t("referral")}</h2>
          <ReferralSection />
        </motion.section>
      )}

      {/* Agents Section */}
      {isAuthenticated && !isOffline && (
        <motion.section
          className="space-y-4"
          variants={staggerItemVariants}
          initial="hidden"
          animate="visible"
        >
          <h2 className="text-lg font-semibold">{t("agents")}</h2>
          <AgentsSection />
        </motion.section>
      )}

      {/* Tutorial Section */}
      <motion.section
        className="space-y-4"
        variants={staggerItemVariants}
        initial="hidden"
        animate="visible"
      >
        <h2 className="text-lg font-semibold">{t("tutorials")}</h2>
        <TutorialSection />
      </motion.section>

      {/* Legal Section */}
      <motion.section
        className="space-y-4"
        variants={staggerItemVariants}
        initial="hidden"
        animate="visible"
      >
        <h2 className="text-lg font-semibold">{t("legal")}</h2>
        <LegalSection />
      </motion.section>
    </motion.div>
  );
}
